import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import Select from 'react-select';
import { brownSelectStyles as customSelectStyles } from '../utils/selectStyles';
import {
  Button,
  Card,
  Collapse,
  Form,
  Badge,
  Alert,
  Row,
  Col,
  Spinner
} from 'react-bootstrap';
import {
  FaSearch,
  FaCheckCircle,
  FaGasPump,
  FaChevronDown,
  FaChevronUp,
  FaRoad,
  FaClock,
  FaTools,
  FaListUl,
  FaClipboardCheck,
  FaHourglassHalf,
  FaExchangeAlt
} from 'react-icons/fa';
import TransfertGasoilModal from '../components/TransfertGasoilModal';
import TableComponentMateriel from '../components/TableComponentMateriel';
import { generatePointageMaterielPDF } from '../utils/pointageMaterielPDF';
import DocumentsModal from '../components/DocumentsModal';
import { normalize, normalizeModeCompteur } from '../utils/normalize';
import KpiCard from '../components/KpiCard';
import { FaTruck } from 'react-icons/fa';

const PointageMaterielPage = ({ user }) => {
  const [materiels, setMateriels] = useState([]);
  const [chantiers, setChantiers] = useState([]);

  const [citernes, setCiternes] = useState([]);
  const [selectedCiterne, setSelectedCiterne] = useState(null);
  const selectedCiterneId = selectedCiterne?.value || null;

  const [selectedChantier, setSelectedChantier] = useState(null);
  const selectedChantierId = selectedChantier?.value || null;
  const selectedChantierName = selectedChantier?.label || '';

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  const [dataTable, setDataTable] = useState([]);
  const [dejaPresents, setDejaPresents] = useState([]);
  const [showDejaPresents, setShowDejaPresents] = useState(true);

  const [loading, setLoading] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocMateriel, setSelectedDocMateriel] = useState(null);
  const [showCiterneDocsModal, setShowCiterneDocsModal] = useState(false);
  const [showAllMateriels, setShowAllMateriels] = useState(false);

  const chantierOptions = useMemo(() => {
    return (chantiers || [])
      .filter((c) => c?.id && c?.name)
      .map((c) => ({ value: c.id, label: String(c.name) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [chantiers]);

  const citerneOptions = useMemo(() => {
    return (citernes || [])
      .filter((c) => c?.id && c?.nom)
      .map((c) => ({
        value: c.id,
        label: `${c.nom} (Stock: ${Number(c.stock_reel_l || 0)} L)`
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [citernes]);

  const selectedCiterneObj = useMemo(() => {
    if (!selectedCiterneId) return null;
    return (citernes || []).find((c) => Number(c.id) === Number(selectedCiterneId)) || null;
  }, [citernes, selectedCiterneId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const materielUrl = showAllMateriels ? '/materiels?all=1' : '/materiels';
        const [resChantiers, resMateriels] = await Promise.all([
          axios.get('/chantiers'),
          axios.get(materielUrl)
        ]);

        setChantiers(Array.isArray(resChantiers.data) ? resChantiers.data : []);
        setMateriels(Array.isArray(resMateriels.data) ? resMateriels.data : []);
      } catch (err) {
        console.error(err);
        setWarningMsg('⚠️ Erreur lors du chargement initial.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showAllMateriels]);

  useEffect(() => {
    const fetchCiternes = async () => {
      setCiternes([]);
      setSelectedCiterne(null);

      if (!selectedChantierId) return;

      try {
        setLoading(true);
        const res = await axios.get(`/citernes-gasoil/chantier/${selectedChantierId}`);
        const list = Array.isArray(res.data) ? res.data : [];
        setCiternes(list);

        if (list.length === 1) {
          setSelectedCiterne({
            value: list[0].id,
            label: `${list[0].nom} (Stock: ${Number(list[0].stock_reel_l || 0)} L)`
          });
        }
      } catch (err) {
        console.error(err);
        setWarningMsg('⚠️ Impossible de charger les citernes pour ce chantier.');
      } finally {
        setLoading(false);
      }
    };

    fetchCiternes();
  }, [selectedChantierId]);

  const buildRowsFromMateriels = (materielsList, dejaIds = [], showAll = false) => {
    const chantierChoisiNorm = normalize(selectedChantierName);
    const chantierId = Number(selectedChantierId || 0);

    const filtered = (materielsList || []).filter((m) => {
      if (normalize(m.etat) === 'bloque') return false;

      if (showAll) return true;

      if (!chantierId) return false;

      if (m.chantier_id !== null && m.chantier_id !== undefined && m.chantier_id !== '') {
        return Number(m.chantier_id) === chantierId;
      }

      const chantierActuel = normalize(m.chantier_actuel || '');
      if (!chantierChoisiNorm) return false;

      return chantierActuel === chantierChoisiNorm || chantierActuel.includes(chantierChoisiNorm);
    });

    return filtered.map((m) => {
      const dejaPresent = dejaIds.includes(Number(m.id));
      return {
        id: m.id,
        matricule: m.matricule || '',
        type: m.type || '',
        societe: m.societe || '',
        chantier_actuel: m.chantier_actuel || '',
        chantier_id: m.chantier_id ?? null,
        etat: m.etat || '',
        mode_compteur: normalizeModeCompteur(m.mode_compteur),
        compteur_prec:
          m.compteur_prec !== null && m.compteur_prec !== undefined && m.compteur_prec !== ''
            ? m.compteur_prec
            : '',
        compteur_nv: '',
        heures_reels: '',
        gasoil: 0,
        reparation: '',
        observation: '',
        dejaPresent
      };
    });
  };

  const handleVerifier = async () => {
    setWarningMsg('');
    setSuccessMsg('');

    if (!date) return;
    if (!selectedChantierId && !showAllMateriels) {
      setDataTable([]);
      setDejaPresents([]);
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`/pointage_materiels?start=${date}&end=${date}`);
      const dejaPointes = (res.data || []).filter((p) => p.date === date);

      const idsPointes = dejaPointes.map((p) => Number(p.id_materiel)).filter(Boolean);

      const chantierChoisiNorm = normalize(selectedChantierName);
      const mats = new Map((materiels || []).map((m) => [Number(m.id), m]));

      const noms = showAllMateriels
        ? idsPointes.map((id) => mats.get(id)).filter(Boolean).map((m) => m.matricule).filter(Boolean)
        : idsPointes
            .map((id) => mats.get(id))
            .filter((m) => m && normalize(m.chantier_actuel) === chantierChoisiNorm)
            .map((m) => m.matricule)
            .filter(Boolean);

      setDejaPresents(noms);
      setDataTable(buildRowsFromMateriels(materiels, idsPointes, showAllMateriels));
    } catch (err) {
      console.error(err);
      setWarningMsg('⚠️ Erreur lors de la vérification.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (date && (selectedChantierId || showAllMateriels) && materiels.length > 0) {
      handleVerifier();
    } else {
      setDataTable([]);
      setDejaPresents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedChantierId, selectedChantierName, showAllMateriels, materiels]);

  const handleCellChange = (rowId, field, value) => {
    setDataTable((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const handleOpenDocuments = (row) => {
    setSelectedDocMateriel(row);
    setShowDocsModal(true);
  };

  const handleValider = async () => {
    setWarningMsg('');
    setSuccessMsg('');

    if (!selectedChantierId && !showAllMateriels) {
      return setWarningMsg('❗ Veuillez sélectionner un chantier.');
    }

    if (!date) {
      return setWarningMsg('❗ Veuillez choisir une date.');
    }

    const rawPayload = dataTable
      .filter((r) => !r.dejaPresent)
      .map((r) => ({
        id_materiel: r.id,
        chantier_id: showAllMateriels ? (selectedChantierId || r.chantier_id) : selectedChantierId,
        date,
        mode_compteur: normalizeModeCompteur(r.mode_compteur),
        compteur_prec: r.compteur_prec === '' ? null : Number(r.compteur_prec),
        compteur_nv: r.compteur_nv === '' ? null : Number(r.compteur_nv),
        heures_reels: r.heures_reels === '' ? 0 : Number(r.heures_reels),
        gasoil: r.gasoil === '' ? 0 : Number(r.gasoil),
        gasoil_l: r.gasoil === '' ? 0 : Number(r.gasoil),
        citerne_id: selectedCiterneId || null,
        reparation: r.reparation || '',
        observation: r.observation || ''
      }));

    const payload = rawPayload.filter((x) => {
      const mode = normalizeModeCompteur(x.mode_compteur);

      if (mode === 'km') {
        return (
          (x.compteur_prec !== null && x.compteur_nv !== null) ||
          Number(x.heures_reels || 0) > 0 ||
          Number(x.gasoil_l || 0) > 0 ||
          !!x.reparation ||
          !!x.observation
        );
      }

      return (
        Number(x.heures_reels || 0) > 0 ||
        (x.compteur_prec !== null && x.compteur_nv !== null) ||
        Number(x.gasoil_l || 0) > 0 ||
        !!x.reparation ||
        !!x.observation
      );
    });

    if (!payload.length) {
      return setWarningMsg('⚠️ Aucune ligne valide à enregistrer.');
    }

    const invalidCompteur = payload.find(
      (x) =>
        x.compteur_prec !== null &&
        x.compteur_nv !== null &&
        Number(x.compteur_nv) < Number(x.compteur_prec)
    );

    if (invalidCompteur) {
      return setWarningMsg('❗ Compteur NV doit être supérieur ou égal à compteur PREC.');
    }

    const gasoilWithoutCiterne = payload.some((x) => Number(x.gasoil_l || 0) > 0) && !selectedCiterneId;
    if (gasoilWithoutCiterne) {
      return setWarningMsg('⛽ Une citerne est obligatoire si du gasoil est saisi.');
    }

    try {
      const pdfRows = dataTable
        .filter((r) => payload.some((p) => Number(p.id_materiel) === Number(r.id)))
        .map((r) => ({
          id_materiel: r.id,
          matricule: r.matricule,
          type: r.type,
          chantier_actuel: r.chantier_actuel,
          mode_compteur: normalizeModeCompteur(r.mode_compteur),
          compteur_prec: r.compteur_prec,
          compteur_nv: r.compteur_nv,
          heures_reels: r.heures_reels,
          gasoil: r.gasoil,
          reparation: r.reparation,
          observation: r.observation
        }));

      generatePointageMaterielPDF({
        date,
        userName: user?.name || user?.login || 'Utilisateur',
        chantiersLabel: selectedChantierName,
        rows: pdfRows
      });

      const res = await axios.post('/pointage_materiels', payload);
      const saved = Array.isArray(res.data) ? res.data : [];
      const warnings = saved.map((x) => x?.warning).filter(Boolean);

      if (warnings.length) {
        setWarningMsg(warnings.join(' | '));
      }

      setSuccessMsg('✅ Pointages matériels enregistrés avec succès.');
      await handleVerifier();
    } catch (err) {
      console.error(err);
      setWarningMsg(err?.response?.data?.message || '❌ Erreur lors de l’enregistrement.');
    }
  };

  const stockInfo = useMemo(() => {
    if (!selectedCiterneObj) return null;

    const stock = Number(selectedCiterneObj.stock_reel_l || 0);
    const min = Number(selectedCiterneObj.capacite_min_l || 0);
    const max = Number(selectedCiterneObj.capacite_max_l || 0);

    return {
      stock,
      min,
      max,
      isLow: stock < min,
      isFull: max > 0 && stock >= max
    };
  }, [selectedCiterneObj]);

  const canWork = !!selectedChantierId || showAllMateriels;

  const stats = useMemo(() => {
    const total = dataTable.length;
    const deja = dataTable.filter((r) => r.dejaPresent).length;
    const restants = total - deja;
    const modeH = dataTable.filter((r) => normalizeModeCompteur(r.mode_compteur) === 'h').length;
    const modeKm = dataTable.filter((r) => normalizeModeCompteur(r.mode_compteur) === 'km').length;
    const totalGasoil = dataTable.reduce((sum, r) => sum + Number(r.gasoil || 0), 0);

    return {
      total,
      deja,
      restants,
      modeH,
      modeKm,
      totalGasoil: Number(totalGasoil.toFixed(2))
    };
  }, [dataTable]);

  return (
    <div className="container mt-4 mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <div>
          <h2 className="d-flex align-items-center gap-2 mb-1" style={{ color: '#6d4e36', fontWeight: 800 }}>
            <FaTools /> Pointage matériel
          </h2>
          <div className="text-muted">
            Saisie journalière des matériels par chantier avec suivi compteur, heures et gasoil.
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <Badge bg="light" text="dark" style={{ border: '1px solid #d8c8b8', padding: '10px 12px' }}>
            👤 {user?.login || user?.name || 'Utilisateur'}
          </Badge>
          {selectedChantierName ? (
            <Badge bg="dark" style={{ padding: '10px 12px' }}>
              🏗️ {selectedChantierName}
            </Badge>
          ) : null}
        </div>
      </div>

      {warningMsg ? (
        <Alert variant="warning" className="mt-3" style={{ border: '1px solid #6d4e36' }}>
          {warningMsg}
        </Alert>
      ) : null}

      {successMsg ? (
        <Alert variant="success" className="mt-3" style={{ border: '1px solid #6d4e36' }}>
          {successMsg}
        </Alert>
      ) : null}

      <Card className="mb-3" style={{ borderRadius: '16px', border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)', background: '#fffdf8' }}>
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Label className="fw-bold">📅 Date</Form.Label>
              <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Col>

            <Col md={5}>
              <Form.Label className="fw-bold">🏗️ Chantier</Form.Label>
              <Select
  isClearable
  isSearchable
  styles={customSelectStyles}
  menuPortalTarget={document.body}
  menuPosition="fixed"
  placeholder="Choisir un chantier..."
  options={chantierOptions}
  value={selectedChantier}
  onChange={(val) => setSelectedChantier(val || null)}
/>
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold">🛢️ Citerne</Form.Label>
              <Select
  isClearable
  isSearchable
  styles={customSelectStyles}
  menuPortalTarget={document.body}
  menuPosition="fixed"
  placeholder={!selectedChantierId ? "Choisir d'abord un chantier..." : 'Choisir une citerne...'}
  options={citerneOptions}
  value={selectedCiterne}
  onChange={(val) => setSelectedCiterne(val || null)}
  isDisabled={!selectedChantierId}
/>
            </Col>

            <Col md={12} className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-primary"
                  onClick={handleVerifier}
                  disabled={loading || !canWork}
                >
                  <FaSearch className="me-2" />
                  Vérifier
                </Button>

                <Button
                  variant="primary"
                  onClick={handleValider}
                  disabled={loading || !canWork || !date}
                >
                  <FaClipboardCheck className="me-2" />
                  Valider le pointage
                </Button>

                <Button
                  style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#fff' }}
                  onClick={() => setShowTransferModal(true)}
                >
                  <FaExchangeAlt className="me-2" />
                  Transfert Gasoil
                </Button>
              </div>

              {loading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  Chargement...
                </div>
              ) : (
                <div className="text-muted small">
                  💡 Le chantier charge la liste des matériels disponibles non bloqués.
                </div>
              )}
            </Col>

            <Col md={12}>
              <Form.Check
                type="switch"
                id="switch-all-materiels"
                label={
                  <span>
                    <FaTruck className="me-1" />
                    Afficher tous les matériels (tous chantiers)
                  </span>
                }
                checked={showAllMateriels}
                onChange={(e) => setShowAllMateriels(e.target.checked)}
                style={{ color: '#6d4e36', fontWeight: 600 }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {selectedCiterneObj && stockInfo ? (
        <Card className="mb-3" style={{ borderRadius: '16px', border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)', background: '#fff' }}>
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <strong>🛢️ {selectedCiterneObj.nom}</strong>{' '}
              {stockInfo.isLow ? (
                <Badge bg="warning" text="dark">⚠️ Stock bas</Badge>
              ) : (
                <Badge bg="success">✅ Stock OK</Badge>
              )}
              {stockInfo.isFull ? <Badge bg="secondary" className="ms-2">Max atteint</Badge> : null}
              <div className="text-muted small mt-1">
                Chantier : <strong>{selectedChantierName}</strong>
              </div>
            </div>

            <div className="text-end d-flex flex-column align-items-end gap-2">
              <div><strong>Stock réel :</strong> {stockInfo.stock} L</div>
              <div className="text-muted small">Min : {stockInfo.min} L | Max : {stockInfo.max} L</div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowCiterneDocsModal(true)}
                style={{ fontSize: '0.78rem', borderRadius: 8, padding: '3px 12px' }}
              >
                📎 Docs citerne
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <DocumentsModal
        show={showCiterneDocsModal}
        onHide={() => setShowCiterneDocsModal(false)}
        module="citerne_gasoil"
        moduleId={selectedCiterneObj?.id}
        itemLabel={selectedCiterneObj?.nom || ''}
        title="Documents de la citerne"
      />

      <Row className="g-3 mb-3">
        <Col md={2}>
          <KpiCard title="Total" value={stats.total} sub="Matériels affichés" color="#6d4e36" bg="#f8f2ea" icon={FaTruck} />
        </Col>

        <Col md={2}>
          <KpiCard title="Mode heure" value={stats.modeH} sub="Suivi en heures" color="#0d6efd" bg="#eef7ff" icon={FaClock} />
        </Col>

        <Col md={2}>
          <KpiCard title="Mode KM" value={stats.modeKm} sub="Suivi en kilomètres" color="#3751d1" bg="#eef3ff" icon={FaRoad} />
        </Col>

        <Col md={2}>
          <KpiCard title="Déjà pointés" value={stats.deja} sub="Traités aujourd’hui" color="#198754" bg="#eefbf3" icon={FaCheckCircle} />
        </Col>

        <Col md={2}>
          <KpiCard title="Restants" value={stats.restants} sub="À saisir" color="#d17b00" bg="#fff7ea" icon={FaHourglassHalf} />
        </Col>

        <Col md={2}>
          <KpiCard title="Gasoil saisi" value={stats.totalGasoil} sub="Litres" color="#495057" bg="#f7f8fb" icon={FaGasPump} />
        </Col>
      </Row>

      {canWork && (
        <Card className="mb-3" style={{ borderRadius: '16px', border: '1px solid rgba(109,78,54,0.18)', boxShadow: '0 8px 24px rgba(109,78,54,0.08)' }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <strong className="d-flex align-items-center gap-2">
                  <FaListUl />
                  Déjà pointés
                </strong>
                <div className="text-muted small">
                  {showAllMateriels
                    ? <>Matériels déjà pointés (tous chantiers) à la date <strong>{date}</strong></>
                    : <>Matériels déjà pointés pour <strong>{selectedChantierName}</strong> à la date <strong>{date}</strong></>
                  }
                </div>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowDejaPresents((v) => !v)}
              >
                {showDejaPresents ? <FaChevronUp /> : <FaChevronDown />} Détails
              </Button>
            </div>

            <Collapse in={showDejaPresents}>
              <div className="mt-3">
                {dejaPresents.length === 0 ? (
                  <div className="text-success d-flex align-items-center gap-2">
                    <FaCheckCircle /> Aucun matériel déjà pointé.
                  </div>
                ) : (
                  <Row className="g-2">
                    {dejaPresents.map((m, idx) => (
                      <Col md={3} key={idx}>
                        <Card style={{ background: '#f4f4f4', border: '1px solid #e1e1e1' }}>
                          <Card.Body className="py-2">
                            <strong>{m}</strong>
                            <div className="small text-muted">Déjà pointé</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      )}

      {!canWork ? (
        <Alert variant="warning" style={{ border: '1px solid #6d4e36' }}>
          ✅ Sélectionne un <strong>chantier</strong> ou active <strong>«&nbsp;Afficher tous les matériels&nbsp;»</strong> pour commencer la saisie.
        </Alert>
      ) : (
        <TableComponentMateriel
          data={dataTable}
          onCellChange={handleCellChange}
          onOpenDocuments={handleOpenDocuments}
        />
      )}

      <DocumentsModal
        show={showDocsModal}
        onHide={() => setShowDocsModal(false)}
        module="materiel"
        moduleId={selectedDocMateriel?.id}
        itemLabel={
          selectedDocMateriel
            ? `${selectedDocMateriel.matricule || ''}${selectedDocMateriel.type ? ` - ${selectedDocMateriel.type}` : ''}`
            : ''
        }
        title="Documents du matériel"
        canDelete={canAccess(user, 'pointage-materiels:delete')}
      />

      <TransfertGasoilModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        sourceCiternes={citernes}
        onSuccess={() => {
          if (selectedChantierId) {
            axios.get(`/citernes-gasoil/chantier/${selectedChantierId}`)
              .then((res) => setCiternes(Array.isArray(res.data) ? res.data : []))
              .catch(() => {});
          }
        }}
      />
    </div>
  );
};

export default PointageMaterielPage;